import { ShippingTestMethodDefinitionDto } from "@intelligems/ig-types";

type SyntheticConditionValue = NonNullable<
  NonNullable<
    ShippingTestMethodDefinitionDto["deleteTargetFreeCondition"]
  >["min"]
>;

export const isSameShippingSelection = (
  left: Pick<
    ShippingTestMethodDefinitionDto,
    | "deliveryProfileId"
    | "deliveryLocationGroupId"
    | "deliveryZoneId"
    | "methodDefinitionId"
    | "canonicalMethodId"
    | "deleteTargetKind"
    | "deleteTargetRateDefinitionId"
    | "deleteTargetFreeCondition"
  >,
  right: Pick<
    ShippingTestMethodDefinitionDto,
    | "deliveryProfileId"
    | "deliveryLocationGroupId"
    | "deliveryZoneId"
    | "methodDefinitionId"
    | "canonicalMethodId"
    | "deleteTargetKind"
    | "deleteTargetRateDefinitionId"
    | "deleteTargetFreeCondition"
  >
) => {
  if (!left.methodDefinitionId || !right.methodDefinitionId) {
    return false;
  }

  return (
    getShippingSelectionIdentity(left) ===
      getShippingSelectionIdentity(right) ||
    isCanonicalSyntheticRateSelectionAlias(left, right)
  );
};

const isCanonicalSyntheticRateSelectionAlias = (
  left: Pick<
    ShippingTestMethodDefinitionDto,
    | "deliveryProfileId"
    | "deliveryLocationGroupId"
    | "deliveryZoneId"
    | "methodDefinitionId"
    | "canonicalMethodId"
    | "deleteTargetKind"
    | "deleteTargetRateDefinitionId"
  >,
  right: Pick<
    ShippingTestMethodDefinitionDto,
    | "deliveryProfileId"
    | "deliveryLocationGroupId"
    | "deliveryZoneId"
    | "methodDefinitionId"
    | "canonicalMethodId"
    | "deleteTargetKind"
    | "deleteTargetRateDefinitionId"
  >
) => {
  const [canonicalTarget, syntheticRateTarget] =
    left.deleteTargetKind === "canonical_method" &&
    right.deleteTargetKind === "synthetic_rate_definition"
      ? [left, right]
      : left.deleteTargetKind === "synthetic_rate_definition" &&
          right.deleteTargetKind === "canonical_method"
        ? [right, left]
        : [null, null];

  if (!canonicalTarget || !syntheticRateTarget) {
    return false;
  }

  if (
    !syntheticRateTarget.deleteTargetRateDefinitionId ||
    getLegacySource(syntheticRateTarget.methodDefinitionId) ||
    getLegacySourceId(syntheticRateTarget.methodDefinitionId)
  ) {
    return false;
  }

  return (
    canonicalTarget.deliveryProfileId ===
      syntheticRateTarget.deliveryProfileId &&
    canonicalTarget.deliveryLocationGroupId ===
      syntheticRateTarget.deliveryLocationGroupId &&
    canonicalTarget.deliveryZoneId === syntheticRateTarget.deliveryZoneId &&
    getCanonicalMethodId(canonicalTarget) ===
      getCanonicalMethodId(syntheticRateTarget)
  );
};

const getShippingSelectionIdentity = (
  definition: Pick<
    ShippingTestMethodDefinitionDto,
    | "deliveryProfileId"
    | "deliveryLocationGroupId"
    | "deliveryZoneId"
    | "methodDefinitionId"
    | "canonicalMethodId"
    | "deleteTargetKind"
    | "deleteTargetRateDefinitionId"
    | "deleteTargetFreeCondition"
  >
) => {
  const scopeKey = [
    definition.deliveryProfileId ?? "",
    definition.deliveryLocationGroupId ?? "",
    definition.deliveryZoneId ?? "",
  ].join("|");

  const deleteTargetKind = definition.deleteTargetKind ?? "canonical_method";

  if (deleteTargetKind === "synthetic_rate_definition") {
    return [
      scopeKey,
      deleteTargetKind,
      definition.deleteTargetRateDefinitionId ??
        getLegacySourceId(definition.methodDefinitionId) ??
        getCanonicalMethodId(definition),
    ].join("|");
  }

  if (deleteTargetKind === "synthetic_free_condition") {
    const freeCondition = definition.deleteTargetFreeCondition;
    return [
      scopeKey,
      deleteTargetKind,
      freeCondition?.subject ?? "",
      serializeConditionValue(freeCondition?.min),
      serializeConditionValue(freeCondition?.max),
    ].join("|");
  }

  return [scopeKey, deleteTargetKind, getCanonicalMethodId(definition)].join(
    "|"
  );
};

const getCanonicalMethodId = (
  definition: Pick<
    ShippingTestMethodDefinitionDto,
    "canonicalMethodId" | "methodDefinitionId"
  >
) => {
  const canonicalMethodId = definition.canonicalMethodId;
  if (canonicalMethodId) return canonicalMethodId;

  const methodDefinitionId = definition.methodDefinitionId;
  if (!methodDefinitionId) return "";

  const sourceIndex = methodDefinitionId.indexOf("?source=");
  return sourceIndex === -1
    ? methodDefinitionId
    : methodDefinitionId.slice(0, sourceIndex);
};

const getLegacySource = (methodDefinitionId?: string | null) => {
  if (!methodDefinitionId) return undefined;
  const [, query = ""] = methodDefinitionId.split("?");
  if (!query) return undefined;
  return new URLSearchParams(query).get("source") ?? undefined;
};

const getLegacySourceId = (methodDefinitionId?: string | null) => {
  if (!methodDefinitionId) return undefined;
  const [, query = ""] = methodDefinitionId.split("?");
  if (!query) return undefined;
  return new URLSearchParams(query).get("source_id") ?? undefined;
};

const serializeConditionValue = (
  value: SyntheticConditionValue | null | undefined
) => {
  if (!value) return "";

  if (value.__typename === "MoneyV2") {
    return `money:${value.amount}:${value.currencyCode ?? ""}`;
  }

  if (value.__typename === "Weight") {
    return `weight:${value.unit}:${value.value}`;
  }

  return JSON.stringify(value);
};
